export const DEFAULT_TEAM_ROLE = 'viewer'
export const NO_ACCESS_TEAM_ROLE = 'no_access'

export const TEAM_PERMISSION_KEYS = [
    'can_manage_quiz',
    'can_copy',
    'can_manage_integrations',
    'can_view_crm',
    'can_share',
    'can_transfer',
    'can_delete',
    'can_publish',
]

export const TEAM_PERMISSIONS = [
    {
        key: 'can_manage_quiz',
        labelKey: 'canManageQuestionsLabel',
        descriptionKey: 'canManageQuestionsDescription',
    },
    {
        key: 'can_copy',
        labelKey: 'canCopyQuizLabel',
        descriptionKey: 'canCopyQuizDescription',
    },
    {
        key: 'can_manage_integrations',
        labelKey: 'canManageIntegrationsLabel',
        descriptionKey: 'canManageIntegrationsDescription',
    },
    {
        key: 'can_view_crm',
        labelKey: 'canViewLeadsLabel',
        descriptionKey: 'canViewLeadsDescription',
    },
    {
        key: 'can_share',
        labelKey: 'canShareLabel',
        descriptionKey: 'canShareDescription',
    },
    {
        key: 'can_transfer',
        labelKey: 'canTransferLabel',
        descriptionKey: 'canTransferDescription',
    },
    {
        key: 'can_delete',
        labelKey: 'canDeleteLabel',
        descriptionKey: 'canDeleteDescription',
    },
    {
        key: 'can_publish',
        labelKey: 'canPublishLabel',
        descriptionKey: 'canPublishDescription',
    },
]

export const TEAM_ROLE_PRESETS = {
    viewer: {
        labelKey: 'viewerTeamRole',
        permissions: ['can_view_crm'],
    },
    editor: {
        labelKey: 'editorTeamRole',
        permissions: ['can_manage_quiz', 'can_copy', 'can_publish'],
    },
    manager: {
        labelKey: 'managerTeamRole',
        permissions: [
            'can_manage_quiz',
            'can_copy',
            'can_manage_integrations',
            'can_view_crm',
            'can_share',
            'can_publish',
        ],
    },
    admin: {
        labelKey: 'adminTeamRole',
        permissions: [...TEAM_PERMISSION_KEYS],
    },
}

export function getTeamRolePreset(role = DEFAULT_TEAM_ROLE) {
    return TEAM_ROLE_PRESETS[role] || TEAM_ROLE_PRESETS[DEFAULT_TEAM_ROLE]
}

export function createEmptyPermissions() {
    return TEAM_PERMISSION_KEYS.reduce((acc, key) => {
        acc[key] = false
        return acc
    }, {})
}

export function createPermissionsFromRole(role = DEFAULT_TEAM_ROLE) {
    const permissions = createEmptyPermissions()
    const preset = getTeamRolePreset(role)

    preset.permissions.forEach(key => {
        permissions[key] = true
    })

    return permissions
}

export function getRolePermissionsCount(role = DEFAULT_TEAM_ROLE) {
    return getTeamRolePreset(role).permissions.length
}

export function getRoleLabelKey(role = DEFAULT_TEAM_ROLE) {
    if (role === NO_ACCESS_TEAM_ROLE) {
        return 'noAccessTeamRole'
    }

    return getTeamRolePreset(role).labelKey
}

export function normalizeBackendPermissions(source = {}) {
    return TEAM_PERMISSION_KEYS.reduce((acc, key) => {
        acc[key] = Boolean(source?.[key])
        return acc
    }, {})
}

export function getPermissionsCount(permissions = {}) {
    const normalizedPermissions = normalizeBackendPermissions(permissions)

    return TEAM_PERMISSION_KEYS.filter(key => normalizedPermissions[key]).length
}

export function getRoleByPermissions(permissions = {}) {
    const normalizedPermissions = normalizeBackendPermissions(permissions)

    const enabledPermissions = TEAM_PERMISSION_KEYS
        .filter(key => normalizedPermissions[key])
        .sort()

    if (!enabledPermissions.length) {
        return NO_ACCESS_TEAM_ROLE
    }

    const matchedRole = Object.entries(TEAM_ROLE_PRESETS).find(([, preset]) => {
        return JSON.stringify([...preset.permissions].sort()) === JSON.stringify(enabledPermissions)
    })

    if (matchedRole) {
        return matchedRole[0]
    }

    if (TEAM_PERMISSION_KEYS.every(key => normalizedPermissions[key])) {
        return 'admin'
    }

    if (normalizedPermissions.can_manage_integrations || normalizedPermissions.can_share) {
        return 'manager'
    }

    if (normalizedPermissions.can_manage_quiz || normalizedPermissions.can_copy || normalizedPermissions.can_publish) {
        return 'editor'
    }

    return DEFAULT_TEAM_ROLE
}

export function mapCollaboratorFromBackend(collaborator = {}) {
    const permissions = normalizeBackendPermissions(collaborator)
    const role = getRoleByPermissions(permissions)

    return {
        id: collaborator.id,
        email: collaborator.user_email || collaborator.user?.email || '',
        name: collaborator.user_name || collaborator.user?.name || '',
        status: collaborator.status,
        expiresAt: collaborator.expires_at || null,
        role,
        permissions,
        isNew: false,
        isRoleOpen: false,
        isAccessOpen: false,
    }
}

export function mapViewerPermissionsToTeamRole(viewerPermissions = {}) {
    const permissions = normalizeBackendPermissions(viewerPermissions)

    return getRoleByPermissions(permissions)
}

export function mapMemberToShareQuizInput(member = {}) {
    return {
        email: member.email,
        permissions: member.permissions || createPermissionsFromRole(member.role || DEFAULT_TEAM_ROLE),
        expires_at: member.expiresAt || null,
    }
}
